import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious,
} from "@/components/ui/carousel";
import { ArrowRight, ArrowUpRight, Sparkles, Leaf, Compass } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import BookingForm from "@/components/BookingForm";
import { api } from "@/lib/api";
import { HERO_BG, ABOUT_PORTRAIT, SAGE_TEXTURE, PILLAR_IMAGES } from "@/lib/images";

// Ikony filarow trzymane we froncie (mapowane po indeksie). Zdjecia pochodza
// z tresci (p.image / *_image) z fallbackiem na domyslne PILLAR_IMAGES.
const PILLAR_ICONS = [Leaf, Sparkles, Compass];

export default function LandingPage() {
  const [posts, setPosts] = useState([]);
  const [content, setContent] = useState(null);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    api.get("/content").then(({ data }) => setContent(data)).catch(() => setLoadError(true));
    api.get("/blog").then(({ data }) => setPosts(data.slice(0, 3))).catch(() => setPosts([]));
  }, []);

  const scrollTo = (id) => {
    const el = document.querySelector(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  if (loadError) {
    return (
      <div className="min-h-screen bg-[#F9F8F6] flex items-center justify-center px-6">
        <div className="text-center">
          <p className="font-display text-2xl text-[#2B2B2B] mb-3">Nie udało się załadować treści strony.</p>
          <p className="text-[#5C6670]">Odśwież stronę za chwilę. Jeśli problem się powtarza, skontaktuj się z administratorem.</p>
        </div>
      </div>
    );
  }

  if (!content) {
    return (
      <div className="min-h-screen bg-[#F9F8F6] flex items-center justify-center">
        <div className="w-10 h-10 rounded-full border-2 border-[#E5E0D8] border-t-[#8A9A86] animate-spin" />
      </div>
    );
  }

  const { hero, about, offer, philosophy, testimonials, booking, faq } = content;
  const pillars = (offer?.pillars) || [];
  const heroBg = hero?.background_image || HERO_BG;
  const aboutPortrait = about?.portrait_image || ABOUT_PORTRAIT;
  const philosophyBg = philosophy?.background_image || SAGE_TEXTURE;

  return (
    <div className="min-h-screen bg-[#F9F8F6] grain" data-testid="landing-page">
      <Navbar />

      {/* HERO */}
      <section className="relative pt-32 md:pt-40 pb-20 md:pb-32 overflow-hidden" data-testid="hero-section">
        <div
          className="absolute inset-0 opacity-[0.12] pointer-events-none"
          style={{ backgroundImage: `url(${heroBg})`, backgroundSize: "cover", backgroundPosition: "center" }}
        />
        <div className="container-prose relative">
          <div className="grid lg:grid-cols-12 gap-12 items-end">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, ease: "easeOut" }}
              className="lg:col-span-8"
            >
              <p className="text-overline mb-8" data-testid="hero-overline">
                {hero?.overline}
              </p>
              <h1 className="font-display text-5xl sm:text-6xl md:text-7xl lg:text-8xl text-[#2B2B2B] leading-[0.95] tracking-tight mb-8">
                {hero?.heading_before}<em className="text-[#8A9A86] not-italic font-light italic">{hero?.heading_em}</em>{hero?.heading_after}
              </h1>
              <p className="text-lg md:text-xl text-[#5C6670] font-light max-w-2xl leading-relaxed mb-12">
                {hero?.subheading}
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  onClick={() => scrollTo("#rezerwacja")}
                  data-testid="hero-cta-button"
                  className="bg-[#2B2B2B] text-white rounded-full px-8 py-4 hover:bg-[#8A9A86] transition-colors duration-300 font-medium text-sm tracking-wide inline-flex items-center justify-center gap-2 group"
                >
                  {hero?.cta_primary}
                  <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                </button>
                <button
                  onClick={() => scrollTo("#oferta")}
                  data-testid="hero-explore-button"
                  className="border border-[#2B2B2B]/20 text-[#2B2B2B] rounded-full px-8 py-4 hover:border-[#8A9A86] hover:text-[#8A9A86] transition-colors font-medium text-sm tracking-wide"
                >
                  {hero?.cta_secondary}
                </button>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1.2, delay: 0.4 }}
              className="lg:col-span-4 hidden lg:block"
            >
              <div className="space-y-6 text-sm text-[#5C6670] border-l border-[#E5E0D8] pl-8">
                {(hero?.stats || []).map((s, i) => (
                  <div key={i}>
                    <p className="text-overline mb-2">{s.value}</p>
                    <p>{s.label}</p>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* O MNIE */}
      <section id="o-mnie" className="py-24 md:py-32" data-testid="about-section">
        <div className="container-prose">
          <div className="grid lg:grid-cols-12 gap-12 lg:gap-20 items-center">
            <div className="lg:col-span-5 relative">
              <div className="aspect-[4/5] rounded-2xl overflow-hidden bg-[#E5E0D8]">
                <img src={aboutPortrait} alt="Agnieszka Sendlewska" className="w-full h-full object-cover" />
              </div>
              <div className="absolute -bottom-6 -right-6 hidden md:block bg-white rounded-2xl p-6 shadow-[0_8px_30px_rgb(0,0,0,0.06)] border border-[#E5E0D8] max-w-[220px]">
                <p className="text-overline mb-2">Filozofia pracy</p>
                <p className="text-sm text-[#2B2B2B] font-light leading-relaxed italic">{about?.card_quote}</p>
              </div>
            </div>

            <div className="lg:col-span-7">
              <p className="text-overline mb-6">{about?.overline}</p>
              <h2 className="font-display text-4xl md:text-5xl lg:text-6xl text-[#2B2B2B] leading-[1.05] mb-8">
                {about?.heading}
              </h2>
              <div className="space-y-6 text-[#5C6670] text-base md:text-lg leading-relaxed font-light max-w-xl">
                {(about?.paragraphs || []).map((p, i) => (
                  <p key={i}>{p}</p>
                ))}
              </div>

              <div className="mt-10 grid grid-cols-3 gap-4 max-w-md">
                {(about?.stats || []).map((s, i) => (
                  <Stat key={i} n={s.n} l={s.l} />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* OFERTA - PILLARS */}
      <section id="oferta" className="py-24 md:py-32 bg-[#F2EEE7]" data-testid="pillars-section">
        <div className="container-prose">
          <div className="max-w-3xl mb-16 md:mb-24">
            <p className="text-overline mb-6">{offer?.overline}</p>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl text-[#2B2B2B] leading-[1.05] mb-6">
              {offer?.heading}
            </h2>
            <p className="text-[#5C6670] text-lg font-light leading-relaxed">
              {offer?.intro}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {pillars.map((p, i) => {
              const Icon = PILLAR_ICONS[i % PILLAR_ICONS.length];
              const image = p.image || PILLAR_IMAGES[i % PILLAR_IMAGES.length];
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.7, delay: i * 0.1 }}
                  className="group bg-white rounded-2xl overflow-hidden border border-[#E5E0D8] hover:-translate-y-1 transition-transform duration-500"
                  data-testid={`pillar-card-${i}`}
                >
                  <div className="aspect-[4/3] overflow-hidden">
                    <img src={image} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  </div>
                  <div className="p-8 lg:p-10">
                    <div className="flex items-center gap-3 mb-4">
                      <Icon size={18} className="text-[#8A9A86]" />
                      <p className="text-overline">{p.overline}</p>
                    </div>
                    <h3 className="font-display text-2xl md:text-3xl text-[#2B2B2B] mb-4 leading-tight">{p.title}</h3>
                    <p className="text-[#5C6670] font-light leading-relaxed mb-6 text-sm">{p.desc}</p>
                    <ul className="space-y-2 border-t border-[#E5E0D8] pt-5">
                      {(p.services || []).map((s) => (
                        <li key={s} className="text-sm text-[#2B2B2B] flex items-start gap-2">
                          <span className="text-[#8A9A86] mt-1">·</span> {s}
                        </li>
                      ))}
                    </ul>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* PHILOSOPHY STRIP */}
      <section className="py-24 md:py-32 bg-[#2B2B2B] text-white relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-10"
          style={{ backgroundImage: `url(${philosophyBg})`, backgroundSize: "cover", backgroundPosition: "center" }}
        />
        <div className="container-prose relative">
          <div className="max-w-4xl">
            <p className="text-overline text-[#8A9A86] mb-8">{philosophy?.overline}</p>
            <blockquote className="font-display text-3xl md:text-5xl lg:text-6xl leading-[1.15] font-light">
              {philosophy?.quote}
            </blockquote>
          </div>
        </div>
      </section>

      {/* OPINIE */}
      <section id="opinie" className="py-24 md:py-32" data-testid="testimonials-section">
        <div className="container-prose">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-16">
            <div className="max-w-2xl">
              <p className="text-overline mb-6">{testimonials?.overline}</p>
              <h2 className="font-display text-4xl md:text-5xl lg:text-6xl text-[#2B2B2B] leading-[1.05]">
                {testimonials?.heading}
              </h2>
            </div>
          </div>

          <Carousel opts={{ align: "start", loop: true }} className="w-full">
            <CarouselContent className="-ml-6">
              {(testimonials?.items || []).map((t, i) => (
                <CarouselItem key={i} className="pl-6 md:basis-1/2 lg:basis-1/2" data-testid={`testimonial-${i}`}>
                  <div className="bg-white rounded-2xl border border-[#E5E0D8] p-10 lg:p-12 h-full flex flex-col">
                    <span className="font-display text-7xl text-[#8A9A86] leading-none mb-4">{`"`}</span>
                    <p className="font-display text-2xl md:text-3xl text-[#2B2B2B] leading-snug font-light italic flex-1 mb-8">
                      {t.quote}
                    </p>
                    <div className="flex items-center justify-between pt-6 border-t border-[#E5E0D8]">
                      <p className="text-sm text-[#2B2B2B] font-medium">{t.author}</p>
                      <p className="text-xs uppercase tracking-widest text-[#8A9A86]">{t.context}</p>
                    </div>
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
            <div className="flex gap-3 mt-10">
              <CarouselPrevious data-testid="testimonial-prev" className="static translate-y-0 border-[#E5E0D8] text-[#2B2B2B] hover:bg-[#8A9A86] hover:text-white hover:border-[#8A9A86]" />
              <CarouselNext data-testid="testimonial-next" className="static translate-y-0 border-[#E5E0D8] text-[#2B2B2B] hover:bg-[#8A9A86] hover:text-white hover:border-[#8A9A86]" />
            </div>
          </Carousel>
        </div>
      </section>

      {/* BLOG */}
      <section id="blog" className="py-24 md:py-32 bg-[#F2EEE7]" data-testid="blog-section">
        <div className="container-prose">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-16">
            <div className="max-w-2xl">
              <p className="text-overline mb-6">Baza wiedzy</p>
              <h2 className="font-display text-4xl md:text-5xl lg:text-6xl text-[#2B2B2B] leading-[1.05]">
                Czytaj, zanim umówisz.
              </h2>
            </div>
            <Link
              to="/blog" data-testid="blog-all-link"
              className="text-sm text-[#2B2B2B] hover:text-[#8A9A86] transition-colors inline-flex items-center gap-2 group"
            >
              Wszystkie artykuły <ArrowUpRight size={16} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
            </Link>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {posts.map((p, i) => (
              <Link
                to={`/blog/${p.slug}`}
                key={p.id}
                data-testid={`blog-card-${i}`}
                className="group bg-white rounded-2xl overflow-hidden border border-[#E5E0D8] hover:-translate-y-1 transition-transform duration-500"
              >
                <div className="aspect-[4/3] overflow-hidden bg-[#E5E0D8]">
                  {p.cover_image && (
                    <img src={p.cover_image} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  )}
                </div>
                <div className="p-8">
                  <p className="text-overline mb-4">{p.category}</p>
                  <h3 className="font-display text-2xl text-[#2B2B2B] leading-tight mb-3 group-hover:text-[#8A9A86] transition-colors">{p.title}</h3>
                  <p className="text-sm text-[#5C6670] font-light leading-relaxed line-clamp-3">{p.excerpt}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* REZERWACJA */}
      <section id="rezerwacja" className="py-24 md:py-32" data-testid="booking-section">
        <div className="container-prose">
          <div className="grid lg:grid-cols-12 gap-12 lg:gap-16">
            <div className="lg:col-span-4">
              <p className="text-overline mb-6">{booking?.overline}</p>
              <h2 className="font-display text-4xl md:text-5xl lg:text-6xl text-[#2B2B2B] leading-[1.05] mb-8">
                {booking?.heading}
              </h2>
              <p className="text-[#5C6670] font-light leading-relaxed mb-8">
                {booking?.intro}
              </p>
              <div className="space-y-4 text-sm text-[#5C6670] border-t border-[#E5E0D8] pt-6">
                {(booking?.rows || []).map((r, i) => (
                  <div key={i} className="flex justify-between"><span>{r.k}</span><span className="text-[#2B2B2B]">{r.v}</span></div>
                ))}
              </div>
            </div>
            <div className="lg:col-span-8">
              <BookingForm pillars={pillars} />
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-24 md:py-32 bg-[#F2EEE7]" data-testid="faq-section">
        <div className="container-prose">
          <div className="grid lg:grid-cols-12 gap-12 lg:gap-16">
            <div className="lg:col-span-5">
              <p className="text-overline mb-6">{faq?.overline}</p>
              <h2 className="font-display text-4xl md:text-5xl lg:text-6xl text-[#2B2B2B] leading-[1.05] mb-8">
                {faq?.heading}
              </h2>
              <p className="text-[#5C6670] font-light leading-relaxed">
                {faq?.intro}
              </p>
            </div>
            <div className="lg:col-span-7">
              <Accordion type="single" collapsible className="w-full">
                {(faq?.items || []).map((f, i) => (
                  <AccordionItem value={`item-${i}`} key={i} className="border-b border-[#E5E0D8]" data-testid={`faq-item-${i}`}>
                    <AccordionTrigger className="text-left text-lg md:text-xl text-[#2B2B2B] font-display font-medium py-6 hover:no-underline hover:text-[#8A9A86]">
                      {f.q}
                    </AccordionTrigger>
                    <AccordionContent className="text-[#5C6670] font-light leading-relaxed pb-6 text-base">
                      {f.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

function Stat({ n, l }) {
  return (
    <div>
      <p className="font-display text-3xl md:text-4xl text-[#2B2B2B] mb-1">{n}</p>
      <p className="text-xs text-[#5C6670] uppercase tracking-widest">{l}</p>
    </div>
  );
}
